import type { SgDashboardData, SgDashboardCardConfig, SgSemaphoreLevel } from './types';

function levelFromCounts(critical: number, warning: number): SgSemaphoreLevel {
  if (critical > 0) return 'critical';
  if (warning > 0) return 'warning';
  return 'ok';
}

function levelFromPercent(percent: number, warningBelow = 70, criticalBelow = 40): SgSemaphoreLevel {
  if (percent < criticalBelow) return 'critical';
  if (percent < warningBelow) return 'warning';
  return 'ok';
}

export function buildSgDashboardCards(data: SgDashboardData): SgDashboardCardConfig[] {
  const cards: SgDashboardCardConfig[] = [
    {
      id: 'nc',
      title: 'No conformidades',
      permission: 'registros.non_conformities.access',
      to: '/registros/no-conformidades',
      primaryLabel: 'Abiertas',
      primaryValue: data.nonConformities.open,
      secondaryLabel: 'Vencidas',
      secondaryValue: data.nonConformities.overdue,
      level: levelFromCounts(data.nonConformities.overdue, data.nonConformities.open),
    },
    {
      id: 'capa',
      title: 'Acciones CAPA',
      permission: 'actions_inbox.access',
      to: '/acciones',
      primaryLabel: 'Pendientes',
      primaryValue: data.capa.pending,
      secondaryLabel: 'Vencidas',
      secondaryValue: data.capa.overdue,
      level: levelFromCounts(data.capa.overdue, data.capa.pending),
    },
    {
      id: 'objectives',
      title: 'Objetivos',
      permission: 'objectives.access',
      to: '/objetivos',
      primaryLabel: 'En riesgo',
      primaryValue: data.objectives.atRisk,
      secondaryLabel: 'Total período',
      secondaryValue: data.objectives.total,
      level: levelFromCounts(0, data.objectives.atRisk),
    },
    {
      id: 'audits',
      title: 'Auditorías',
      permission: 'audits.access',
      to: '/auditorias',
      primaryLabel: 'Próximas (7 días)',
      primaryValue: data.audits.upcoming,
      secondaryLabel: 'Vencidas',
      secondaryValue: data.audits.overdue,
      level: levelFromCounts(data.audits.overdue, data.audits.upcoming),
    },
    {
      id: 'legal',
      title: 'Requisitos legales',
      permission: 'legal_requirements.access',
      to: '/requisitos-legales',
      primaryLabel: 'Por vencer',
      primaryValue: data.legalRequirements.expiring,
      secondaryLabel: 'Vencidos',
      secondaryValue: data.legalRequirements.overdue,
      level: levelFromCounts(data.legalRequirements.overdue, data.legalRequirements.expiring),
    },
    {
      id: 'doc-ack',
      title: 'Acuse de lectura',
      permission: 'documents.access',
      to: '/documentos',
      primaryLabel: 'Pendientes míos',
      primaryValue: data.documentAcknowledgements?.pendingForUser ?? 0,
      level: levelFromCounts(0, data.documentAcknowledgements?.pendingForUser ?? 0),
    },
    {
      id: 'risks',
      title: 'Riesgos',
      permission: 'risks.access',
      to: '/riesgos/tablero',
      primaryLabel: 'Críticos abiertos',
      primaryValue: data.risks.critical,
      secondaryLabel: 'Altos abiertos',
      secondaryValue: data.risks.high,
      level: levelFromCounts(data.risks.critical, data.risks.high),
    },
    {
      id: 'changes',
      title: 'Gestión del cambio',
      permission: 'change_management.access',
      to: '/gestion-cambios',
      primaryLabel: 'Cambios en el período',
      primaryValue: data.changes.totalInPeriod,
      level: data.changes.totalInPeriod > 0 ? 'warning' : 'ok',
    },
  ];

  if (data.safetyIncidents) {
    cards.push({
      id: 'safety',
      title: 'Incidentes SST',
      permission: 'iso45001.incidents.access',
      to: '/iso45001/incidentes',
      primaryLabel: 'En el período',
      primaryValue: data.safetyIncidents.totalInPeriod,
      secondaryLabel: 'Abiertos',
      secondaryValue: data.safetyIncidents.openInPeriod,
      level: levelFromCounts(0, data.safetyIncidents.openInPeriod),
    });
  }

  if (data.soaCoverage) {
    cards.push({
      id: 'soa',
      title: 'SoA (ISO 27001)',
      permission: 'iso27001.soa.access',
      to: '/iso27001/soa',
      primaryLabel: 'Controles aplicables',
      primaryValue: data.soaCoverage.applicableCount,
      percentLabel: 'Implementados / verificados',
      percentValue: data.soaCoverage.implementedPercent,
      level: levelFromPercent(data.soaCoverage.implementedPercent),
    });
  }

  if (data.environmentalAspects) {
    cards.push({
      id: 'aspects',
      title: 'Aspectos ambientales',
      permission: 'iso14001.aspects.access',
      to: '/iso14001/aspectos',
      primaryLabel: 'Significativos sin control',
      primaryValue: data.environmentalAspects.significantWithoutControl,
      secondaryLabel: 'Significativos totales',
      secondaryValue: data.environmentalAspects.significantCount,
      level: levelFromCounts(
        data.environmentalAspects.significantWithoutControl,
        data.environmentalAspects.significantWithoutControl,
      ),
    });
  }

  if (data.environmentalIndicators) {
    cards.push({
      id: 'env-indicators',
      title: 'Consumos ambientales',
      permission: 'objectives.access',
      to: '/objetivos',
      primaryLabel: 'Fuera de meta',
      primaryValue: data.environmentalIndicators.offTarget,
      secondaryLabel: 'Sin medición',
      secondaryValue: data.environmentalIndicators.missing,
      level: levelFromCounts(
        data.environmentalIndicators.offTarget,
        data.environmentalIndicators.missing,
      ),
    });
  }

  return cards;
}
